import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { TextField } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { CommentActions } from "hooks/useComments";
import MentionsDropdown from "./MentionsDropdown";

interface CommentComposerProps {
  actions: CommentActions;
  /** Rows shown before the field starts to grow. */
  rows?: number;
  placeholder?: string;
}

const MAX_ROWS = 8;

const CommentComposer: React.FC<CommentComposerProps> = ({
  actions,
  rows = 3,
  placeholder,
}) => {
  const { t } = useTranslation();
  // React 18 ids carry colons, and jsdom throws measuring an autosized
  // textarea whose id has one, which the DS default id does.
  const idBase = useId().replace(/:/g, "");
  const helperId = `comment-help-${idBase}`;
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const busy = actions.addingComment || actions.loadingComments;
  const canPost = !actions.isDraft && actions.hasDraft && !busy;
  const mentionCount = actions.insertedMentions.size;
  const internalOnly = t("All comments are internal only");

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // The mention picker handles its own keys first and claims them.
    if (event.defaultPrevented) return;
    const isSubmitChord =
      event.key === "Enter" &&
      (event.metaKey || event.ctrlKey || event.shiftKey);
    if (isSubmitChord && !actions.isDraft) {
      event.preventDefault();
      if (canPost) actions.submit();
    } else if (event.key === "Escape") {
      actions.setMentionDropdownOpen(false);
    }
  };

  return (
    <Stack gap="var(--space-3)">
      <Typography
        id={helperId}
        variant="caption"
        sx={{ color: "var(--color-text-tertiary)" }}
      >
        {mentionCount > 0
          ? `${t("Will notify")} ${mentionCount} ${mentionCount === 1 ? t("person") : t("people")} • ${internalOnly}`
          : internalOnly}
      </Typography>

      <Box ref={setAnchorEl}>
        <TextField
          id={`comment-${idBase}`}
          multiline
          minRows={rows}
          maxRows={Math.max(rows, MAX_ROWS)}
          fullWidth
          name="comment"
          placeholder={placeholder ?? t("AddComment")}
          value={actions.displayComment}
          onChange={actions.handleCommentChange}
          onKeyDown={handleKeyDown}
          inputProps={{
            "aria-label": t("Comment with mentions"),
            "aria-describedby": helperId,
          }}
        />
      </Box>
      <MentionsDropdown
        open={actions.mentionDropdownOpen}
        query={actions.mentionQuery}
        anchorEl={anchorEl}
        onSelect={actions.handleMentionSelect}
        onClose={() => actions.setMentionDropdownOpen(false)}
      />

      {!actions.isDraft && (
        <Inline justify="end" gap="var(--space-3)">
          <Button
            variant="default-soft"
            onClick={actions.reset}
            disabled={!actions.hasDraft || actions.addingComment}
          >
            {t("Clear")}
          </Button>
          <Button
            variant="primary-filled"
            onClick={() => actions.submit()}
            disabled={!canPost}
          >
            {actions.addingComment ? <CircularProgress size={20} /> : t("Post")}
          </Button>
        </Inline>
      )}
    </Stack>
  );
};

export default CommentComposer;
